'use client';

import { c as _c } from "react/compiler-runtime";
import type { DialogProps } from '@radix-ui/react-dialog';
import { Description as DialogPrimitiveDescription } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import * as React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
const Command = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
Command.displayName = CommandPrimitive.displayName;

// Radix assigns the description's id, so the dialog learns it from the rendered element and hands
// it to the input's aria-describedby.
const CommandDialogDescriptionIdContext = React.createContext<string | undefined>(undefined);
const CommandDialog = t0 => {
  const $ = _c(19);
  let children;
  let description;
  let onEscapeKeyDown;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      description,
      onEscapeKeyDown,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = description;
    $[3] = onEscapeKeyDown;
    $[4] = props;
  } else {
    children = $[1];
    description = $[2];
    onEscapeKeyDown = $[3];
    props = $[4];
  }
  const [descriptionId, setDescriptionId] = React.useState();
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = node => {
      setDescriptionId(node?.id);
    };
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const descriptionRef = t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <VisuallyHidden asChild={true}><DialogTitle>Command Menu</DialogTitle></VisuallyHidden>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== description) {
    t3 = <DialogPrimitiveDescription ref={descriptionRef} className="sr-only">{description}</DialogPrimitiveDescription>;
    $[7] = description;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== descriptionId) {
    t4 = <CommandDialogDescriptionIdContext value={descriptionId}>{children}</CommandDialogDescriptionIdContext>;
    $[9] = children;
    $[10] = descriptionId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== onEscapeKeyDown || $[13] !== t3 || $[14] !== t4) {
    t5 = <DialogContent onEscapeKeyDown={onEscapeKeyDown} overlayClassName="bg-black/40 data-[state=open]:animate-none data-[state=closed]:animate-none" className="top-[15%] w-[calc(100%-1.5rem)] max-w-[640px] gap-0 overflow-hidden rounded-xl bg-popover p-0 shadow-2xl !translate-y-0 !duration-0 !transition-none sm:rounded-xl [&>[data-dialog-close]]:hidden">{t2}{t3}{t4}</DialogContent>;
    $[12] = onEscapeKeyDown;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== props || $[17] !== t5) {
    t6 = <Dialog {...props}>{t5}</Dialog>;
    $[16] = props;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
const CommandInput = t0 => {
  const $ = _c(26);
  let badge;
  let className;
  let onKeyDown;
  let props;
  let ref;
  let trailing;
  let wrapperClassName;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      wrapperClassName,
      badge,
      trailing,
      onKeyDown,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = badge;
    $[2] = className;
    $[3] = onKeyDown;
    $[4] = props;
    $[5] = ref;
    $[6] = trailing;
    $[7] = wrapperClassName;
  } else {
    badge = $[1];
    className = $[2];
    onKeyDown = $[3];
    props = $[4];
    ref = $[5];
    trailing = $[6];
    wrapperClassName = $[7];
  }
  const descriptionId = React.use(CommandDialogDescriptionIdContext);
  let t1;
  if ($[8] !== wrapperClassName) {
    t1 = cn("flex items-center border-b px-3", wrapperClassName);
    $[8] = wrapperClassName;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Search className="mr-2 size-4 shrink-0 opacity-50" />;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== className) {
    t3 = cn("flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50", className);
    $[11] = className;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== descriptionId || $[14] !== onKeyDown || $[15] !== props || $[16] !== ref || $[17] !== t3) {
    t4 = <CommandPrimitive.Input ref={ref} aria-describedby={descriptionId} className={t3} onKeyDown={onKeyDown} {...props} />;
    $[13] = descriptionId;
    $[14] = onKeyDown;
    $[15] = props;
    $[16] = ref;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== trailing) {
    t5 = trailing && <span className="ml-2 flex shrink-0 items-center">{trailing}</span>;
    $[19] = trailing;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== badge || $[22] !== t1 || $[23] !== t4 || $[24] !== t5) {
    t6 = <div className={t1} cmdk-input-wrapper="">{t2}{badge}{t4}{t5}</div>;
    $[21] = badge;
    $[22] = t1;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
};
CommandInput.displayName = CommandPrimitive.Input.displayName;
const CommandList = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive.List ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
CommandList.displayName = CommandPrimitive.List.displayName;
const CommandEmpty = t0 => {
  const $ = _c(6);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  let t1;
  if ($[3] !== props || $[4] !== ref) {
    t1 = <CommandPrimitive.Empty ref={ref} className="py-6 text-center text-sm" {...props} />;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;
const CommandLoading = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("py-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive.Loading ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
CommandLoading.displayName = CommandPrimitive.Loading.displayName;
const CommandGroup = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive.Group ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
CommandGroup.displayName = CommandPrimitive.Group.displayName;
const CommandSeparator = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("-mx-1 h-px bg-border", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive.Separator ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;
const CommandItem = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive.Item ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
CommandItem.displayName = CommandPrimitive.Item.displayName;
const CommandShortcut = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("ml-auto text-xs tracking-widest text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
CommandShortcut.displayName = 'CommandShortcut';
export { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandLoading, CommandSeparator, CommandShortcut };